'use client';

import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { cn } from '@workspace/ui/lib/utils';
import { Icon } from '@/src/components/ui';
import Image from 'next/image';

type Props = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
};

export function Lightbox({ open, onClose, children, className }: Props) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const stopBubbling = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      data-no-card-click="true"
      className="fixed inset-0 z-[999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      onClick={stopBubbling}
      onPointerDown={stopBubbling}
    >
      <div
        data-no-card-click="true"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />

      <div
        data-no-card-click="true"
        className={cn(
          'relative z-10',
          'w-[92vw] sm:w-[88vw] md:w-[80vw] lg:w-[72vw] xl:w-[60vw]',
          'max-w-[1100px]',
          'max-h-[85vh]',
          'rounded-lg bg-white shadow-xl',
          'p-3 md:p-4',
          'overflow-hidden',
          className
        )}
        onClick={stopBubbling}
        onPointerDown={stopBubbling}
      >
        {children}

        <button
          data-no-card-click="true"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-3 right-3 rounded-full bg-black/60 p-2 hover:cursor-pointer group"
          aria-label="Close"
        >
          <Icon
            name="close"
            className="w-4 h-4 text-white group-hover:text-blue-primary transition-colors duration-200"
          />
        </button>
      </div>
    </div>
  );
}

export const ImagesGallery = ({
  urls,
  isOne,
  url,
  className = '',
  imageSize,
}: {
  className?: string;
  urls?: string[];
  isOne: boolean;
  url?: string;
  imageSize?: number;
}) => {
  const [open, setOpen] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);

  const images = useMemo(() => {
    if (isOne && url) return [url];
    return urls ?? [];
  }, [isOne, url, urls]);

  const close = useCallback(() => setOpen(false), []);

  const showPrev = useCallback(() => {
    setCurrentIdx((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const showNext = useCallback(() => {
    setCurrentIdx((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const openAt = (idx: number) => {
    setCurrentIdx(idx);
    setOpen(true);
  };

  if (isOne && url) {
    return (
      <>
        <div data-no-card-click="true" className={cn('w-full md:h-[290px]')}>
          <Image
            src={url}
            className="object-cover rounded-md h-full w-full"
            onClick={(e) => {
              e.stopPropagation();
              openAt(0);
            }}
            alt="gallery image"
            loading="lazy"
            priority={false}
            width={400}
            height={290}
          />
        </div>

        <Lightbox open={open} onClose={close}>
          <div className="relative w-full h-[70vh] sm:h-[72vh] md:h-[75vh] lg:h-[78vh] flex items-center justify-center">
            <div className="relative w-full h-full max-w-[1000px] mx-auto p-2 md:p-4">
              <div className="relative w-full h-full min-h-[260px] md:min-h-[420px]">
                <Image
                  src={images[currentIdx] as string}
                  alt=""
                  fill
                  sizes="(max-width: 640px) 92vw, (max-width: 1024px) 80vw, 60vw"
                  className="object-contain"
                  data-no-card-click="true"
                />
              </div>
            </div>
          </div>
        </Lightbox>
      </>
    );
  }

  return (
    <>
      <div
        className={cn('grid grid-cols-4 gap-1 md:gap-2', className)}
      >
        {images.map((u, idx) => (
          <button
            key={idx}
            type="button"
            data-no-card-click="true"
            className={cn(
              'relative w-full aspect-square overflow-hidden rounded-md bg-black/5',
              'cursor-pointer'
            )}
            onClick={(e) => {
              e.stopPropagation();
              openAt(idx);
            }}
          >
            <Image
              src={u}
              alt="gallery image"
              fill
              sizes="(max-width: 768px) 25vw, 160px"
              className="object-cover"
              data-no-card-click="true"
              priority={false}
            />
          </button>
        ))}
      </div>

      <Lightbox open={open} onClose={close}>
        <div className="relative w-full flex items-center justify-center">
          {images.length > 0 && (
            <Image
              src={images[currentIdx] as string}
              alt=""
              className="max-h-[80vh] w-auto object-contain"
              width={1400}
              height={900}
              data-no-card-click="true"
            />
          )}

          {images.length > 1 && (
            <>
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  showPrev();
                }}
                className="absolute left-2 top-1/2 -translate-y-1/2 group bg-black/50 rounded-full p-2 hover:cursor-pointer"
                data-no-card-click="true"
                aria-label="Previous image"
              >
                <Icon
                  name="chevron-left"
                  className="w-5 h-5 text-white group-hover:text-blue-primary transition-colors duration-200"
                />
              </button>

              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  showNext();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 rounded-full p-2 hover:cursor-pointer group"
                data-no-card-click="true"
                aria-label="Next image"
              >
                <Icon
                  name="chevron-left"
                  className="rotate-180 w-5 h-5 text-white group-hover:text-blue-primary transition-colors duration-200"
                />
              </button>
            </>
          )}
        </div>

        {images.length > 1 && (
          <div className="mt-3 text-sm text-gray-600 text-center">
            {currentIdx + 1} / {images.length}
          </div>
        )}
      </Lightbox>
    </>
  );
};
